import React, { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { VZenTable } from "../../../../../components/VZenTable";
import CustomContextMenu from "../../../../../components/global_components/CustomContextMenu";
import { TableRowProps, defaultTableRowRenderer } from "react-virtualized";
import { CellProps, Column } from "react-table";
import useCustomContextMenu from "./hooks/useCustomContextMenu";
import InspectionApplicationCell from "./components/InspectionApplicationCell";
import InspectionScheduledCell from "./components/InspectionScheduledCell";
import InspectionStatusCell from "./components/InspectionStatusCell";
import { InspectionData } from "./constants/CitizenInspection";
import InspectionRequestModal from "../inspection-request-modal/InspectionRequestModal";
import usePublicInspections from "../../../../tanstack/inspections/public/usePublicInspections";
import { faRotateRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export default function CitizenInspectionList() {
  const { data: inspectionData } = usePublicInspections();
  const { site } = useParams<{ site: string }>();
  const history = useHistory();
  const { setContextMenuLink, handleOpenContextMenu, setShowPopup, showPopup, contextMenuLink, popupPosition } =
    useCustomContextMenu();

  const getInspectionCitizenUrl = (site: string, inspectionId: string) => {
    return `/${site}/public/permits/inspections/${inspectionId}`;
  };

  const [requestModalInspectionId, setRequestModalInspectionId] = useState<string | null>(null);
  const requestModalOpen = requestModalInspectionId !== null;

  const onRequestModalOpen = (id: string) => {
    setRequestModalInspectionId(id);
  };
  const onRequestModalClose = () => {
    setRequestModalInspectionId(null);
  };

  const columns: Column<InspectionData>[] = [
    {
      Header: "Inspection Type",
      width: "400",
      Cell: (cellProps: CellProps<InspectionData>) => {
        const inspectionType = cellProps.row.original.attributes.title;
        const isReInspection = !!cellProps.row.original.attributes.previousInspectionId;
        return (
          <div>
            {inspectionType}
            {isReInspection && (
              <div className='subText'>
                {" "}
                <FontAwesomeIcon className='tw-mr-1' icon={faRotateRight} />
                Reinspection
              </div>
            )}
          </div>
        );
      },
    },
    {
      Header: "Scheduled / Performed",
      width: "400",
      Cell: (cellProps: CellProps<InspectionData>) => {
        return <InspectionScheduledCell cellProps={cellProps} onRequestInspection={onRequestModalOpen} />;
      },
    },
    {
      Header: "Status",
      width: "400",
      Cell: (cellProps: CellProps<InspectionData>) => {
        return <InspectionStatusCell cellProps={cellProps} />;
      },
    },
    {
      Header: "Application",
      width: "300",
      Cell: (cellProps: CellProps<InspectionData>) => {
        return <InspectionApplicationCell cellProps={cellProps} />;
      },
    },
  ];

  const containerProps = {
    style: {
      display: "block",
      minHeight: Math.max(300, (inspectionData?.data.length ?? 0) * 90 + 50),
      minWidth: 900,
    },
  };

  const tableProps = {
    headerHeight: 50,
    rowHeight: 80,
    // Adding custom row renderer to allow for tabbing through the confirmation numbers, which are accessible links
    rowRenderer: (props: TableRowProps) => {
      const id = props.rowData.original.id;
      const url = getInspectionCitizenUrl(site, id);

      const rightClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setContextMenuLink(url);
        handleOpenContextMenu(e);
      };

      const onRowClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (e.metaKey || e.ctrlKey) {
          window.open(url, "_blank");
        } else {
          history.push(url);
        }
      };

      return (
        //Removed the onRowClick props from VZenTable, and wrapped the row in this div with onClicks, to stop the row itself from being focusable
        <div role='presentation' onClick={(event) => onRowClick(event)} onContextMenu={(event) => rightClick(event)}>
          {defaultTableRowRenderer(props)}
        </div>
      );
    },
  };

  return (
    <>
      {showPopup && (
        <CustomContextMenu closePopup={setShowPopup} x={popupPosition.x} y={popupPosition.y} url={contextMenuLink} />
      )}
      {requestModalOpen && (
        <InspectionRequestModal
          inspectionId={requestModalInspectionId}
          isOpen={requestModalOpen}
          onClose={onRequestModalClose}
        />
      )}
      <VZenTable<InspectionData>
        columns={columns}
        data={inspectionData?.data ?? []}
        initialState={{}}
        containerProps={containerProps}
        tableProps={tableProps}
      />
    </>
  );
}
